import { injectable } from "@launchtray/tsyringe-async";
import { Bindable, nameof, NotifyProperty } from "bindable-data";
import { BoosterType } from "./data/BoosterType";
import { SaveModel } from "./SaveModel";
import { Event } from "event-system";
import { NotifyMap } from "../../notifyproperties/NotifyMap";
import { BoxReward } from "../../scenes/game/components/3d/gamefield/box/BoxReward";
import { GameElement } from "../../scenes/game/models/field/GameElement";
import { WeaponType } from "../../scenes/game/models/data/WeaponType";
import { BoostersConfiguration } from "./BoostersConfiguration";
import { BoosterData } from "./data/BoosterData";

@injectable()
export class PlayerModel extends Bindable {
    public readonly applyBooster: Event<BoosterType, number>; // level
    public readonly applyHealth: Event<number>;
    public readonly applyWeapon: Event<WeaponType, number>;
    public readonly resurrectCharacter: Event<void>;
    public readonly syncCoinsWithoutAnimation: Event<void>;

    public readonly coins: NotifyProperty<number>;
    public readonly currentLevel: NotifyProperty<number>;
    public readonly boosters: NotifyMap<BoosterType, BoosterData>;
    public readonly lastBoxReward: NotifyProperty<BoxReward>;

    public constructor(
        private readonly saveModel: SaveModel,
        private readonly boostersConfiguration: BoostersConfiguration
    ) {
        super();

        this.applyBooster = new Event<BoosterType, number>();
        this.applyHealth = new Event<number>();
        this.applyWeapon = new Event<WeaponType, number>();
        this.resurrectCharacter = new Event();
        this.syncCoinsWithoutAnimation = new Event();

        this.coins = new NotifyProperty(this, nameof(this, "coins"), this.saveModel.read("PlayerModel", "coins", "number", 0));
        this.currentLevel = new NotifyProperty(this, nameof(this, "currentLevel"), this.saveModel.read("PlayerModel", "currentLevel", "number", 1));
        const defaultBoosters: [BoosterType, BoosterData][] = [
            [BoosterType.Mine, [1, 0, 1]],
            [BoosterType.Freeze, [1, 0, 1]],
            [BoosterType.Energy, [1, 0, 1]],
            [BoosterType.Turret, [1, 0, 1]]
        ];
        this.boosters = new NotifyMap<BoosterType, BoosterData>(this, nameof(this, "boosters"), this.saveModel.read("PlayerModel", "boosters", "map", defaultBoosters));
        this.lastBoxReward = new NotifyProperty<BoxReward>(this, nameof(this, "lastBoxReward"), null);
    }

    public addCoins(value: number): void {
        this.coins.setValue(this.coins.value + value);
        this.saveModel.write("PlayerModel", "coins", "number", this.coins.value);
    }

    public purchaseBooster(type: BoosterType): boolean {
        const [count, usageCount, level] = this.boosters.get(type);
        const boosterInfo = this.boostersConfiguration.getConfiguration(type, level);
        if (this.coins.value < boosterInfo.price) {
            return false;
        }
        this.coins.setValue(this.coins.value - boosterInfo.price);
        this.boosters.set(type, [count + 1, usageCount, level]);
        this.saveModel.write("PlayerModel", "coins", "number", this.coins.value);
        this.saveModel.write("PlayerModel", "boosters", "map", this.boosters);
        this.syncCoinsWithoutAnimation.emit();
        return true;
    }

    public addBooster(type: BoosterType, count: number = 1): void {
        const [currentCount, usageCount, level] = this.boosters.get(type);
        this.boosters.set(type, [currentCount + count, usageCount, level]);
        this.saveModel.write("PlayerModel", "boosters", "map", this.boosters);
    }

    public subtractBooster(booster: BoosterType): void {
        let [count, usageCount, level] = this.boosters.get(booster);
        if (count == 0) {
            return;
        }
        this.applyBooster.emit(booster, level);

        count--;
        usageCount++;
        const nextLevelConfig = this.boostersConfiguration.getConfiguration(booster, level + 1);
        if (nextLevelConfig != null && nextLevelConfig.usages === usageCount) {
            level++;
        }
        this.boosters.set(booster, [count, usageCount, level]);
        this.saveModel.write("PlayerModel", "boosters", "map", this.boosters);
    }

    public completeLevel(): void {
        this.currentLevel.setValue(this.currentLevel.value + 1);
        this.saveModel.write("PlayerModel", "currentLevel", "number", this.currentLevel.value);
    }

    public applyDoubleBoxReward(): BoxReward {
        if (this.lastBoxReward.value != null) {
            const reward = this.lastBoxReward.value;
            this.parseReward(reward);
            this.syncCoinsWithoutAnimation.emit();
            this.clearDoubleBoxReward();
            return reward;
        } else {
            console.warn(`PlayerModel try to multiply lastBoxReward=${null}`);
        }
    }

    public setDoubleBoxReward(reward: BoxReward): void {
        this.lastBoxReward.setValue(reward);
    }

    public clearDoubleBoxReward(): void {
        this.lastBoxReward.setValue(null);
    }

    private parseReward(reward: BoxReward): void {
        switch (reward.type) {
            case GameElement.BoosterBox: {
                if (reward.coins) {
                    this.addCoins(reward.coins);
                } else if (reward.booster) {
                    this.addBooster(reward.booster.type, reward.booster.count);
                }
                break;
            }
            case GameElement.HealBox: {
                if (reward.heal) {
                    this.applyHealth.emit(reward.heal);
                }
                break;
            }
            case GameElement.WeaponsBox: {
                if (reward.weapon) {
                    this.applyWeapon.emit(reward.weapon.type, reward.weapon.ammo);
                }
                break;
            }
            default: {
                throw new Error(`PlayerModel can't handle applyBoxReward of type=${reward.type}`);
            }
        }
    }

    public resurrect(): void {
        this.resurrectCharacter.emit();
    }
}
